/**
 * Utilities for converting between time format (hh:mm:ss.ms) and decimal seconds
 */

/**
 * Converts time string to decimal seconds
 * Accepts flexible formats:
 *   "45.494" => 45.494 (ss.ms → 00:00:45.494)
 *   "01:45.494" => 105.494 (mm:ss.ms → 00:01:45.494)
 *   "01:23:45.123" => 5025.123 (hh:mm:ss.ms)
 */
export function timeToSeconds(timeString: string): number {
    if (!timeString || timeString.trim() === '') {
        return 0;
    }

    const parts = timeString.trim().split(':');
    let hours = 0;
    let minutes = 0;
    let seconds = 0;
    let milliseconds = 0;

    if (parts.length === 1) {
        // Format: "ss.ms" or "ss"
        const secondsAndMs = parts[0].split('.');
        seconds = parseInt(secondsAndMs[0], 10) || 0;
        milliseconds = secondsAndMs.length > 1 ? parseInt(secondsAndMs[1].padEnd(3, '0').substring(0, 3), 10) || 0 : 0;
    } else if (parts.length === 2) {
        // Format: "mm:ss.ms" or "mm:ss"
        minutes = parseInt(parts[0], 10) || 0;
        const secondsAndMs = parts[1].split('.');
        seconds = parseInt(secondsAndMs[0], 10) || 0;
        milliseconds = secondsAndMs.length > 1 ? parseInt(secondsAndMs[1].padEnd(3, '0').substring(0, 3), 10) || 0 : 0;
    } else if (parts.length === 3) {
        // Format: "hh:mm:ss.ms" or "hh:mm:ss"
        hours = parseInt(parts[0], 10) || 0;
        minutes = parseInt(parts[1], 10) || 0;
        const secondsAndMs = parts[2].split('.');
        seconds = parseInt(secondsAndMs[0], 10) || 0;
        milliseconds = secondsAndMs.length > 1 ? parseInt(secondsAndMs[1].padEnd(3, '0').substring(0, 3), 10) || 0 : 0;
    } else {
        return 0;
    }

    return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
}

/**
 * Converts decimal seconds to time string (hh:mm:ss.ms)
 * Examples:
 *   5025.123 => "01:23:45.123"
 *   630.5    => "00:10:30.500"
 *   7200.0   => "02:00:00.000"
 */
export function secondsToTime(totalSeconds: number): string {
    if (isNaN(totalSeconds) || totalSeconds < 0) {
        return "00:00:00.000";
    }

    const hours = Math.floor(totalSeconds / 3600);
    const remainderAfterHours = totalSeconds % 3600;
    const minutes = Math.floor(remainderAfterHours / 60);
    const seconds = Math.floor(remainderAfterHours % 60);
    const milliseconds = Math.round((totalSeconds % 1) * 1000);

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`;
}

/**
 * Validates if a string is in valid time format (hh:mm:ss.ms)
 */
export function isValidTimeFormat(timeString: string): boolean {
    const timeRegex = /^(\d{2}):(\d{2}):(\d{2})\.(\d{3})$/;
    return timeRegex.test(timeString);
}

/**
 * Formats a decimal value based on display format
 * @param value - The numeric value to format
 * @param format - 'decimal' or 'time'
 * @param decimals - Number of decimal places for decimal format (default: 2)
 */
export function formatKpiValue(value: number, format: 'decimal' | 'time', decimals: number = 2): string {
    if (value === null || value === undefined || isNaN(value)) {
        return '-';
    }

    if (format === 'time') {
        return secondsToTime(value);
    }

    return value.toFixed(decimals);
}
